import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminNewsletter, getAdminNewsletters } from '../../../services/admin/newsletterApi';
import {
  CloseButton,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  PrimaryButton,
  SecondaryButton,
} from '../common/AdminModalPrimitives';
import AdminSearchInput from '../common/AdminSearchInput';
import CommunityTablePagination from '../community/CommunityTablePagination';

/**
 * 강조 섹션 카드에 연결할 뉴스레터 선택 팝업
 *
 * 사용자 페이지에 노출되는 "등록 완료" 뉴스레터만 고를 수 있습니다.
 */

const PAGE_SIZE = 8;

export interface SelectedNewsletter {
  id: number;
  title: string;
}

interface NewsletterSelectModalProps {
  selectedId: number | null;
  onSelect: (newsletter: SelectedNewsletter) => void;
  onClose: () => void;
}

const NewsletterSelectModal: React.FC<NewsletterSelectModalProps> = ({ selectedId, onSelect, onClose }) => {
  const [items, setItems] = useState<AdminNewsletter[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<SelectedNewsletter | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAdminNewsletters(page, PAGE_SIZE, false, search);
        if (!mounted) return;
        setItems(response.content ?? []);
        setTotalPages(response.totalPages ?? 0);
      } catch (err: any) {
        if (!mounted) return;
        setError(err.message || '뉴스레터 목록을 불러올 수 없습니다.');
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [page, search]);

  const activeId = picked?.id ?? selectedId;

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}.${month}.${day}.`;
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard $maxWidth="640px" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>뉴스레터 선택</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="닫기">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <BodyColumn>
            <SearchRow>
              <AdminSearchInput
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(0);
                }}
                placeholder="제목 또는 작성자를 입력해주세요."
              />
            </SearchRow>

            <List>
              {loading ? (
                <StateText>불러오는 중...</StateText>
              ) : error ? (
                <StateText>{error}</StateText>
              ) : items.length === 0 ? (
                <StateText>등록 완료된 뉴스레터가 없습니다.</StateText>
              ) : (
                items.map((item) => (
                  <ListItem
                    key={item.newsletter_id}
                    type="button"
                    $active={item.newsletter_id === activeId}
                    onClick={() => setPicked({ id: item.newsletter_id, title: item.title })}
                  >
                    <ItemTitle>{item.title}</ItemTitle>
                    <ItemMeta>
                      {item.author || '-'} · {formatDate(item.published_at || item.created_at)}
                    </ItemMeta>
                  </ListItem>
                ))
              )}
            </List>

            {totalPages > 1 && (
              <CommunityTablePagination
                currentPage={page + 1}
                totalPages={totalPages}
                onPageChange={(next) => setPage(next - 1)}
              />
            )}
          </BodyColumn>
        </ModalBody>

        <ModalFooter>
          <SecondaryButton type="button" onClick={onClose}>
            취소
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!picked}
            onClick={() => {
              if (picked) onSelect(picked);
            }}
          >
            선택
          </PrimaryButton>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
};

export default NewsletterSelectModal;

const BodyColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// AdminSearchInput 은 가로 flex 행(flex-basis 250px)을 전제로 만들어져 있어 행으로 감싼다.
const SearchRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 200px;
`;

const ListItem = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? '#448181' : 'rgba(112, 115, 124, 0.16)')};
  border-radius: 12px;
  padding: 12px 16px;
  background: ${({ $active }) => ($active ? 'rgba(68, 129, 129, 0.06)' : '#fff')};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(68, 129, 129, 0.06);
  }
`;

const ItemTitle = styled.span`
  display: block;
  width: 100%;
  overflow: hidden;
  color: #171719;
  font-size: 16px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemMeta = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
`;

const StateText = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 15px;
`;
